import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { queryKeys } from "@/lib/queryKeys";
import { invalidateCompanyAssignment } from "@/lib/mutationHelpers";

export const useAllCompanies = () => {
  return useQuery({
    queryKey: queryKeys.companies.all,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cleaning_companies")
        .select("id, company_name")
        .order("company_name", { ascending: true });

      if (error) throw error;
      return data;
    },
  });
};

export const usePropertyCleaningAssignment = (propertyId: string | undefined) => {
  return useQuery({
    queryKey: queryKeys.companies.propertyAssignment(propertyId || ''),
    queryFn: async () => {
      if (!propertyId) return null;
      const { data, error } = await supabase
        .from("property_cleaning_assignments")
        .select("id, company_id, auto_assign_mode, auto_assign_cleaner_id")
        .eq("property_id", propertyId)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!propertyId,
  });
};

export const useUpsertPropertyCleaningAssignment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      propertyId,
      company_id,
      auto_assign_mode,
      auto_assign_cleaner_id,
    }: {
      propertyId: string;
      company_id: string;
      auto_assign_mode: string;
      auto_assign_cleaner_id: string | null;
    }) => {
      const { error } = await supabase
        .from("property_cleaning_assignments")
        .upsert({
          property_id: propertyId,
          company_id,
          auto_assign_mode,
          auto_assign_cleaner_id,
        }, { onConflict: "property_id" });

      if (error) throw error;
      return { propertyId };
    },
    onSuccess: (data) => {
      invalidateCompanyAssignment(queryClient, data.propertyId);
    },
  });
};

export const useDeletePropertyCleaningAssignment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (propertyId: string) => {
      const { error } = await supabase
        .from("property_cleaning_assignments")
        .delete()
        .eq("property_id", propertyId);

      if (error) throw error;
      return { propertyId };
    },
    onSuccess: (data) => {
      invalidateCompanyAssignment(queryClient, data.propertyId);
    },
  });
};

export const useCompanyCleanersForProperty = (companyId: string | null | undefined) => {
  return useQuery({
    queryKey: queryKeys.companies.cleaners(companyId || ''),
    queryFn: async () => {
      if (!companyId) return [];
      const { data, error } = await supabase
        .from("cleaners")
        .select("id, name")
        .eq("company_id", companyId)
        .order("name", { ascending: true });

      if (error) throw error;
      return data;
    },
    enabled: !!companyId,
  });
};
